import { match } from '@/utils/pattern-match-util'
import type { JobStatus } from '@/features/jobs/job-status'
import type {
  ProgressionFlow,
  ProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import {
  matchConsultationDisplayStage,
  type ConsultationDisplayStage,
} from '@/features/jobs/consultations/consultation-display-stage'
import { getProgressionStepContent } from '@/features/jobs/taxes/progression/progression-content'
import { type BookkeepingDisplayStage } from '@/features/jobs/bookkeeping/bookkeeping-display-stage'

/**
 * Gets the display description for a job's status.
 *
 * @param options
 */
export function getStatusDisplayDescription(
  options: GetStatusDisplayTextOptions,
) {
  if (options.consultation) {
    return getStatusDisplayDescriptionForConsultation(options.consultation)
  }

  if (options.bookkeeping) {
    return getStatusDescriptionForBookkeeping(options.bookkeeping)
  }

  return getStatusDisplayDescriptionForJobStatus(options.status)
}

/**
 * Gets the display description for a job's status.
 *
 * @param status
 */
function getStatusDisplayDescriptionForJobStatus(status: JobStatus) {
  return match(status, {
    Unknown: 'Unknown status',
    UnderConstruction: 'Please complete the questionnaire to continue.',
    InfoGathering: 'To continue, please provide all the requested details.',
    Unclaimed: 'A Pro will be with you shortly.',
    Claimed: 'Your Pro is actively working on your request.',
    OnHold:
      'Your request is currently on hold. Please check for any required actions.',
    Idle: `There hasn't been any activity on this request for a while.`,
    Closed: 'Your request has been closed.',
  })
}

/**
 * Gets the display description for a consultation stage.
 *
 * @param stage
 */
function getStatusDisplayDescriptionForConsultation(
  stage: ConsultationDisplayStage,
) {
  return matchConsultationDisplayStage(stage, {
    booked: (stage) =>
      `Good news! We've booked your ${stage.requestedLength} minute consultation with Pro, ${stage.providerDisplayName}, in ${stage.bookedTime.daysUntilStart} days time.`,
    awaitingPickup: (stage) =>
      `We will book a ${stage.requestedLength} minute consultation call based on your availability. Please wait while we connect you to a Pro.`,
    awaitingProConfirmation: () =>
      'Your Pro will find a time that matches your schedule.',
    dayOfCall: (stage) =>
      `Today's the day! Your Pro, ${stage.providerDisplayName}, will be joining you later today for your ${stage.requestedLength} minute consultation call.`,
    inProgress: () => 'Arrange the best time to meet in chat.',
    callCompleted: (stage) =>
      `A transcript of your ${stage.requestedLength} minute consultation call with Pro, ${stage.providerDisplayName}, will be available.`,
  })
}

/**
 * Gets the status description for a bookkeeping stage.
 */
function getStatusDescriptionForBookkeeping(stage: BookkeepingDisplayStage) {
  return match(stage, {
    SettingUpJob: () =>
      'We are currently processing this job. Please check back in a few minutes.',
    PendingAccess: () =>
      'Please connect your platform account for your Pro to access your books.',
    AdminConfirmation: () =>
      'The Pro needs to confirm that they have access to proceed.',
    AwaitingAccessConfirmation: () =>
      'The Pro needs to confirm that they have access to proceed.',
    ReadyToStart: () => 'Your Pro will start working on your books soon.',
    AssessingBooks: () => 'Your Pro is currently assessing your books.',
    InProgress: () => 'Your Pro is currently working on your books.',
    CycleComplete: () =>
      'Your Pro has completed the previous bookkeeping cycle. They will start the next cycle within the first few days of the next month.',
    BookkeepingCompleted: () =>
      'Your Pro has completed your bookkeeping request. Please let your Pro know if you would like to extend your bookkeeping job.',
  })
}

/**
 * Options for {@link getStatusDisplayHeadline}.
 */
export interface GetStatusDisplayTextOptions {
  status: JobStatus
  consultation?: ConsultationDisplayStage | null
  bookkeeping?: BookkeepingDisplayStage | null
  progression?: {
    step: ProgressionStepStatus
    flow: ProgressionFlow
  }
}

/**
 * Gets the display text for a job's status.
 */
export function getStatusDisplayHeadline(
  options: GetStatusDisplayTextOptions,
): string {
  if (options.bookkeeping) {
    return (
      getDisplayHeadlineForBookkeeping(options.bookkeeping) ??
      getDisplayHeadlineForJobStatus(options.status)
    )
  }

  if (options.progression) {
    return (
      getDisplayHeadlineForProgression(options.progression, options.status) ??
      getDisplayHeadlineForJobStatus(options.status)
    )
  }

  if (options.consultation) {
    return (
      getDisplayHeadlineForConsultation(options.consultation) ??
      getDisplayHeadlineForJobStatus(options.status)
    )
  }

  return getDisplayHeadlineForJobStatus(options.status)
}

/**
 * Gets the display text for a consultation stage.
 *
 * @param stage
 */
function getDisplayHeadlineForConsultation(
  stage: ConsultationDisplayStage,
): string | null {
  return matchConsultationDisplayStage(stage, {
    booked: () => 'Consultation booked',
    awaitingPickup: () => 'Awaiting pickup',
    awaitingProConfirmation: () => 'Awaiting confirmation',
    dayOfCall: () => 'Your call is happening today',
    inProgress: () => 'Meet with your Pro',
    callCompleted: () => 'Call completed',
  })
}

/**
 * Gets the display text for a bookkeeping stage.
 */
export function getDisplayHeadlineForBookkeeping(
  stage: BookkeepingDisplayStage,
): string {
  return match(stage, {
    SettingUpJob: () => 'Setting up the job',
    PendingAccess: () => 'Provisioning access',
    AdminConfirmation: () => 'Admin confirmation',
    AwaitingAccessConfirmation: () => 'Awaiting access confirmation',
    ReadyToStart: () => 'Ready to start',
    AssessingBooks: () => 'Assessing books',
    InProgress: () => 'Cycle in progress',
    CycleComplete: () => 'Bookkeeping cycle completed',
    BookkeepingCompleted: () => 'Bookkeeping completed',
  })
}

/**
 * Gets the status display text for a progression.
 *
 * @param progression
 * @param status
 */
function getDisplayHeadlineForProgression(
  progression: Exclude<GetStatusDisplayTextOptions['progression'], undefined>,
  status?: JobStatus,
): string | null {
  if (status === 'Closed') {
    // For closed jobs, don't display job progression if the progression
    // isn't complete.
    switch (progression.step) {
      case 'PreparationCompleted':
      case 'DeliverableCompleted':
      case 'ReviewCompleted':
        break
      default: {
        return getDisplayHeadlineForJobStatus(status)
      }
    }
  }

  const content = getProgressionStepContent({
    flow: progression.flow,
    step: progression.step,
  })
  return content?.title ?? null
}

/**
 * Gets the display text for a job status.
 * @param status
 */
function getDisplayHeadlineForJobStatus(status: JobStatus): string {
  return match(status, {
    Unknown: 'Unknown status',
    UnderConstruction: 'Under construction',
    InfoGathering: 'Complete your checklist',
    Unclaimed: 'Awaiting pickup',
    Claimed: 'In progress',
    OnHold: 'On hold',
    Idle: 'Idle',
    Closed: 'Closed',
  })
}
